/**
 * /settings → Onderhoud (admin-only). Toont de geschiedenis van
 * orphan-GC-runs en geeft de admin een knop om 'm direct te draaien.
 *
 * Dagelijks draait dezelfde cleanup automatisch via Cloudflare Cron
 * (zie wrangler.toml `triggers.crons`); de manual-knop is voor het
 * geval er net een verwijdering heeft plaatsgevonden waarna de admin
 * geen 24h wil wachten voor de viewState-cleanup.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, type RetiredPreviewDTO, type RetiredScopeParam } from "../api/client";
import { confirmDialog } from "../editor/confirmDialog";
import { resolveBilingual } from "../registry";

interface Props {
  workspaceId: string;
}

interface RunRow {
  id: string;
  started_at: string;
  finished_at: string | null;
  trigger_kind: "cron" | "manual";
  triggered_by: string | null;
  workspace_id: string | null;
  orphan_relations_deleted: number;
  orphan_viewstate_concepts_cleaned: number;
  orphan_viewstate_relations_cleaned: number;
  diagrams_touched: number;
  status: "running" | "success" | "error";
  error: string | null;
}

export function MaintenanceSettings({ workspaceId }: Props) {
  const { t } = useTranslation();
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await api.listGcRuns(workspaceId);
      setRuns(res.runs);
    } catch (err) {
      setError(explain(err));
    }
  }, [workspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const runNow = async () => {
    setBusy(true);
    setInfo(null);
    setError(null);
    try {
      const res = await api.runGc(workspaceId);
      const total =
        res.orphan_relations_deleted +
        res.orphan_viewstate_concepts_cleaned +
        res.orphan_viewstate_relations_cleaned;
      setInfo(
        total === 0
          ? t("settings.maintenance.runDoneNothing", { ms: res.duration_ms })
          : t("settings.maintenance.runDoneCleaned", {
              ms: res.duration_ms,
              relations: res.orphan_relations_deleted,
              viewstates: res.orphan_viewstate_concepts_cleaned,
            }),
      );
      await refresh();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <div className="rounded border border-(--ld-border) bg-(--ld-raised) px-3 py-2 text-(--ld-text) text-sm">
        <p className="mb-1">
          <strong>{t("settings.maintenance.introBoldTitle")}</strong>
          {t("settings.maintenance.introAfter")}
        </p>
        <p className="text-(--ld-muted) text-xs">
          {t("settings.maintenance.introDetailBefore")}
          <strong>{t("settings.maintenance.introDetailBold")}</strong>
          {t("settings.maintenance.introDetailAfter")}
        </p>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          className="rounded bg-(--ld-primary) px-3 py-1.5 font-medium text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
          onClick={() => void runNow()}
          disabled={busy}
        >
          {busy ? t("settings.maintenance.runningButton") : t("settings.maintenance.runNowButton")}
        </button>
        <button
          type="button"
          className="rounded border border-(--ld-control) px-3 py-1.5 text-(--ld-text) text-sm hover:bg-(--ld-raised)"
          onClick={() => void refresh()}
          disabled={busy}
        >
          {t("settings.common.refresh")}
        </button>
      </div>

      {error && (
        <p className="rounded bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative) text-sm">
          {error}
        </p>
      )}
      {info && (
        <p className="rounded bg-(--ld-positive-bg) px-3 py-2 text-(--ld-positive-ink) text-sm">
          {info}
        </p>
      )}

      <div>
        <h3 className="mb-2 font-semibold text-(--ld-text) text-sm">
          {t("settings.maintenance.lastRuns")}
        </h3>
        {runs.length === 0 ? (
          <p className="px-3 py-4 text-(--ld-muted) text-sm italic">
            {t("settings.maintenance.empty")}
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-(--ld-border) border-b text-left text-(--ld-muted)">
                <th className="py-1.5 pr-3">{t("settings.maintenance.colWhen")}</th>
                <th className="py-1.5 pr-3">{t("settings.maintenance.colTrigger")}</th>
                <th className="py-1.5 pr-3">{t("settings.maintenance.colStatus")}</th>
                <th className="py-1.5 pr-3 text-right">{t("settings.maintenance.colRelations")}</th>
                <th className="py-1.5 pr-3 text-right">{t("settings.maintenance.colViewState")}</th>
                <th className="py-1.5 pr-3 text-right">{t("settings.maintenance.colDiagrams")}</th>
                <th className="py-1.5 pr-3">{t("settings.maintenance.colWho")}</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-(--ld-rule) border-b">
                  <td className="py-1.5 pr-3 text-(--ld-muted) tabular-nums">
                    {new Date(`${r.started_at}Z`).toLocaleString()}
                  </td>
                  <td className="py-1.5 pr-3 text-(--ld-text)">
                    {r.trigger_kind === "cron"
                      ? t("settings.maintenance.triggerCron")
                      : t("settings.maintenance.triggerManual")}
                  </td>
                  <td className="py-1.5 pr-3">
                    {r.status === "success" ? (
                      <span className="text-(--ld-positive-ink)">
                        {t("settings.maintenance.statusSuccess")}
                      </span>
                    ) : r.status === "error" ? (
                      <span className="text-(--ld-negative)" title={r.error ?? ""}>
                        {t("settings.maintenance.statusError")}
                      </span>
                    ) : (
                      <span className="text-(--ld-muted)">…</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-right">{r.orphan_relations_deleted}</td>
                  <td className="py-1.5 pr-3 text-right">
                    {r.orphan_viewstate_concepts_cleaned + r.orphan_viewstate_relations_cleaned}
                  </td>
                  <td className="py-1.5 pr-3 text-right">{r.diagrams_touched}</td>
                  <td className="py-1.5 pr-3 text-(--ld-muted)">{r.triggered_by ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <hr className="border-(--ld-border)" />
      <RetiredCleanupSection workspaceId={workspaceId} />
    </section>
  );
}

function RetiredCleanupSection({ workspaceId }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [workpackages, setWorkpackages] = useState<Array<{ id: string; name: string }>>([]);
  const [scopeKind, setScopeKind] = useState<"workspace" | "workpackage">("workspace");
  const [workpackageId, setWorkpackageId] = useState<string>("");
  const [preview, setPreview] = useState<RetiredPreviewDTO | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    api
      .listWorkpackages(workspaceId)
      .then((res) => {
        const nonBase = res.workpackages.filter((wp) => !wp.isBase);
        setWorkpackages(nonBase.map((wp) => ({ id: wp.id, name: wp.name })));
        const first = nonBase[0];
        if (first && !workpackageId) setWorkpackageId(first.id);
      })
      .catch(() => {
        // Best-effort — scope-selector valt terug op "workspace only"
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const currentScope = (): RetiredScopeParam =>
    scopeKind === "workpackage" && workpackageId
      ? { kind: "workpackage", workpackageId }
      : { kind: "workspace" };

  const runPreview = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.previewRetired(workspaceId, currentScope());
      setPreview(res);
    } catch (err) {
      setError(explain(err));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const runCleanup = async () => {
    if (!preview) return;
    const ok = await confirmDialog({
      message: t("settings.maintenance.retired.confirmDialog", {
        concepts: preview.concepts.length,
        relations: preview.relations.length,
        diagrams: preview.diagrams.length,
        viewstate: preview.cascade_viewstate_entry_count,
      }),
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.cleanupRetired(workspaceId, currentScope());
      setInfo(
        t("settings.maintenance.retired.cleanupDone", {
          concepts: res.concepts_tombstoned,
          relations: res.relations_tombstoned,
          diagrams: res.diagrams_deleted,
          viewstate: res.viewstate_entries_stripped,
        }),
      );
      setPreview(null);
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h3 className="mb-1 font-semibold text-(--ld-text) text-sm">
          {t("settings.maintenance.retired.heading")}
        </h3>
        <p className="text-(--ld-muted) text-xs">{t("settings.maintenance.retired.intro")}</p>
      </div>

      <div className="flex flex-col gap-2 text-sm">
        <span className="font-medium text-(--ld-text)">
          {t("settings.maintenance.retired.scopeLabel")}
        </span>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            checked={scopeKind === "workspace"}
            onChange={() => {
              setScopeKind("workspace");
              setPreview(null);
            }}
          />
          {t("settings.maintenance.retired.scopeWorkspace")}
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            checked={scopeKind === "workpackage"}
            disabled={workpackages.length === 0}
            onChange={() => {
              setScopeKind("workpackage");
              setPreview(null);
            }}
          />
          {t("settings.maintenance.retired.scopeWorkpackage")}
          <select
            className="rounded border border-(--ld-control) px-2 py-1 text-xs"
            value={workpackageId}
            disabled={scopeKind !== "workpackage" || workpackages.length === 0}
            onChange={(e) => {
              setWorkpackageId(e.target.value);
              setPreview(null);
            }}
          >
            {workpackages.map((wp) => (
              <option key={wp.id} value={wp.id}>
                {wp.name}
              </option>
            ))}
          </select>
        </label>
        <span className="text-(--ld-muted) text-xs">
          {t("settings.maintenance.retired.scopeWorkpackageHint")}
        </span>
      </div>

      <div>
        <button
          type="button"
          className="rounded border border-(--ld-control) px-3 py-1.5 text-(--ld-text) text-sm hover:bg-(--ld-raised) disabled:opacity-50"
          onClick={() => void runPreview()}
          disabled={busy}
        >
          {busy
            ? t("settings.maintenance.retired.previewingButton")
            : t("settings.maintenance.retired.previewButton")}
        </button>
      </div>

      {error && (
        <p className="rounded bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative) text-sm">
          {error}
        </p>
      )}
      {info && (
        <p className="rounded bg-(--ld-positive-bg) px-3 py-2 text-(--ld-positive-ink) text-sm">
          {info}
        </p>
      )}

      {preview && (
        <div className="flex flex-col gap-3 rounded border border-transparent bg-(--ld-notice-bg) px-3 py-3">
          <p className="font-medium text-(--ld-notice-ink) text-sm">
            {t("settings.maintenance.retired.resultHeading")}
          </p>
          {preview.concepts.length === 0 &&
          preview.relations.length === 0 &&
          preview.diagrams.length === 0 ? (
            <p className="text-(--ld-muted) text-sm italic">
              {t("settings.maintenance.retired.previewEmpty")}
            </p>
          ) : (
            <>
              <RetiredList
                heading={t("settings.maintenance.retired.conceptsHeading", {
                  count: preview.concepts.length,
                })}
                items={preview.concepts.map((c) => resolveBilingual(c.name, lang) || c.id)}
              />
              <RetiredList
                heading={t("settings.maintenance.retired.relationsHeading", {
                  count: preview.relations.length,
                })}
                items={preview.relations.map((r) => `${r.type_id} (${r.id.slice(0, 8)})`)}
              />
              <RetiredList
                heading={t("settings.maintenance.retired.diagramsHeading", {
                  count: preview.diagrams.length,
                })}
                items={preview.diagrams.map((d) => d.name)}
              />
              <div className="text-(--ld-text) text-xs">
                <p className="mb-1 font-medium">
                  {t("settings.maintenance.retired.cascadeHeading")}
                </p>
                <ul className="list-disc pl-4">
                  <li>
                    {t("settings.maintenance.retired.cascadeRelations", {
                      count: preview.cascade_relation_count,
                    })}
                  </li>
                  <li>
                    {t("settings.maintenance.retired.cascadeViewState", {
                      entries: preview.cascade_viewstate_entry_count,
                      diagrams: preview.cascade_viewstate_diagram_count,
                    })}
                  </li>
                </ul>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="rounded border border-(--ld-control) px-3 py-1.5 text-(--ld-text) text-sm hover:bg-(--ld-raised)"
                  onClick={() => setPreview(null)}
                  disabled={busy}
                >
                  {t("settings.maintenance.retired.cancelButton")}
                </button>
                <button
                  type="button"
                  className="rounded bg-(--ld-negative) px-3 py-1.5 font-medium text-sm text-white hover:opacity-90 disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
                  onClick={() => void runCleanup()}
                  disabled={busy}
                >
                  {busy
                    ? t("settings.maintenance.retired.cleaningButton")
                    : t("settings.maintenance.retired.confirmButton")}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function RetiredList({ heading, items }: { heading: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div className="text-(--ld-text) text-xs">
      <p className="mb-1 font-medium">{heading}</p>
      <ul className="max-h-32 list-disc overflow-y-auto pl-4">
        {items.map((item, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: read-only preview list, no stable id needed beyond render
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    return err.body?.detail ?? err.body?.title ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return "Unknown error";
}
